import { Box, Button, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'

import practicingSimba from '../../../../assets/simba-ai/practicing.png'
import { useSimbaAIStore } from '../../../../store/simbaai.store'
import { formatDate, getTimePassedFrom, Icons } from '../../../../utils/utilities'
import LatestPracticeStreak from '../LatestPracticeStreak'
import sadSimba from '../../../../assets/simba-ai/sad.png'
import { useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react'
import CustomChip from '../../../common/CustomChip'

const PracticeModeHome = observer( ( { enterFullScreen } ) => {


    const { palette, layout, border, shadows } = useTheme()

    const SimbaAIStore = useSimbaAIStore()

    const setSearchParams = useSearchParams().at( 1 )

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const fetchUserPracticeHistory = async () => {
        SimbaAIStore.setView( "history" )
        await SimbaAIStore.fetchUserHistory()
    }

    const startQuiz = async ( practiceSession ) => {
        setSearchParamsValues( { practiceQuiz: practiceSession._id } )

        SimbaAIStore.setView( "loading" )
        const { success, data } = await SimbaAIStore.fetchPracticeSessionDetails( practiceSession._id )

        if ( success ) {
            if ( data.incomplete_session_exists ) {
                SimbaAIStore.setView( "continue-pending", "left", false )
            } else {
                if ( data.practiced ) {
                    SimbaAIStore.setView( "start-again", "left", false )
                } else {
                    const success = await SimbaAIStore.fetchPracticeSessionQuestions( practiceSession._id )
                    if ( success ) {
                        SimbaAIStore.setView( "question", "left", false )
                        enterFullScreen()
                    }
                }
            }
        }
    }

    const repeatLastOne = () => {
        const lastAttemptDetails = SimbaAIStore.getPracticeHistory.find( item => item.status === "completed" )
        startQuiz( lastAttemptDetails.practice_session )
    }

    const showSubjectPracticeSessions = ( subject ) => {
        setSearchParamsValues( { selectedSubject: subject.subject_code } )
        SimbaAIStore.setView( "subject-practice-sessions" )
    }

    useEffect( () => {
        const getHistory = () => {
            SimbaAIStore.fetchUserHistory()
        }
        getHistory()
    }, [SimbaAIStore] )

    return (
        <Box display="flex" gap={layout.sectionsGap} flexDirection="column" flexGrow={1} >
            <Box flexGrow={1} display="flex" flexDirection="column" gap="10px" padding={layout.pagePadding} overflow="auto" >
                {SimbaAIStore.getPracticeSessions.length > 0 && <LatestPracticeStreak />}
                <Box
                    width="100%"
                    bgcolor={palette.primary.light}
                    borderRadius="10px"
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ sm: "40px", xs: "10px" }}
                    padding="20px"
                    alignItems="center"
                >
                    <Box component="img" height={{ sm: "140px", xs: "100px" }} src={practicingSimba} />
                    <Box display="flex" flexDirection="column" alignItems={{ xs: "center", sm: "flex-start" }}>
                        <Typography textAlign={{ xs: "center", sm: "left" }} color={palette.common.white} variant='h5' fontSize={{ sm: "28px", xs: "20px" }} fontWeight="800" component="p" > <Typography component="span" fontSize="inherit" fontWeight="inherit"  >Simba AI</Typography> Welcomes You to Practice Zone </Typography>
                        <Typography textAlign={{ xs: "center", sm: "left" }} color={palette.common.white} component="p" display="block" maxWidth="450px" fontSize="12px">Get ready for an interactive AI-powered quiz experience created from your own course material. Let’s sharpen your skills, question by question!</Typography>
                        <Box flexWrap="wrap" flexGrow={{ xs: "1", sm: "initial" }} marginTop="30px" display="flex" gap={layout.cardsGap} alignItems="center" >
                            <Button
                                className="animation-wave"
                                onClick={fetchUserPracticeHistory}
                                sx={{
                                    border: `1px solid ${palette.common.white}`,
                                    borderBottom: `4px solid ${palette.common.white}`,
                                    color: palette.common.white,
                                    borderRadius: "10px"
                                }}
                                startIcon={Icons.default.History}
                                variant='outlined'
                                color='primary'
                                disableElevation
                            >
                                View Practice History
                            </Button>
                        </Box>
                    </Box>

                </Box>
                {SimbaAIStore.getTopicsAvailableSubjects.length > 0 && <Box width="100%">
                    <Typography fontSize="14px" color='textSecondary' marginY="10px" fontWeight={600} component="p"  >
                        Select a subject below to get started
                    </Typography>
                    <Box display="grid" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "1fr", sm: "1fr 1fr" }} gap="10px" flexWrap="wrap" alignItems="center">
                        {SimbaAIStore.getTopicsAvailableSubjects.map( subject => (
                            <Box
                                onClick={() => showSubjectPracticeSessions( subject )}
                                component="button"
                                flexGrow={{ xs: 1, sm: "initial" }}
                                overflow="hidden"
                                key={subject.subject_code}
                                display="flex"
                                gap="20px"
                                alignItems="center"
                                padding="10px 20px"
                                borderRadius="10px"
                                boxShadow={shadows[1]}
                                border={border[1]}
                                borderBottom={`4px solid ${palette.borderColor}`}
                                position="relative"
                                bgcolor={palette.contentBg}
                                sx={{
                                    cursor: "pointer",
                                    transition: "200ms",
                                    ":hover": {
                                        borderColor: palette.primary.light,
                                        transform: `skew(-5deg)`
                                    }
                                }}
                            >
                                <Box display="flex" flexShrink={0} fontSize="20px" justifyContent="center" width="40px" sx={{ aspectRatio: "1 / 1" }} borderRadius="50%" bgcolor={palette.primary.dark} color={palette.primary.contrastText} alignItems="center" height="100%">{Icons.default.Subjects}</Box>
                                <Box display="flex" minWidth={0} overflow="hidden" flexDirection="column" alignItems="flex-start">
                                    <Tooltip title={subject.subject_name}>
                                        <Typography textAlign="left" width="100%" fontSize={{ xs: "14px", sm: "18px" }} noWrap fontWeight={600}>{subject.subject_name}</Typography>
                                    </Tooltip>
                                    <Typography display="block" marginTop="-3px" fontSize="12px" component="em">{subject.subject_code}</Typography>
                                </Box>
                            </Box>
                        ) )}
                    </Box>
                </Box>}
                {SimbaAIStore.getPracticeSessions.length > 0 && <Box display="flex" flexDirection="column" width="100%" marginTop="10px">
                    <Typography fontSize="14px" color='textSecondary' marginY="10px" fontWeight={600} component="p"   >
                        Or select a recently added practice session
                    </Typography>
                    <Box display="grid" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "1fr", sm: "1fr 1fr" }} flexWrap="wrap" width="100%" gap={layout.cardsGap}>
                        {SimbaAIStore.getPracticeSessions.slice( 0, 3 ).map( practiceSession => {
                            const topics = Object.values( practiceSession.modules_and_topics ).map( mod => mod.topics ).flat()
                            const modules = Object.keys( practiceSession.modules_and_topics )
                            return (
                                <Box
                                    component="button"
                                    onClick={() => startQuiz( practiceSession )}
                                    flexGrow={{ xs: 1, sm: "initial" }}
                                    overflow="hidden"
                                    key={practiceSession._id}
                                    display="flex"
                                    gap="20px"
                                    alignItems="flex-start"
                                    padding="10px 20px"
                                    borderRadius="10px"
                                    boxShadow={shadows[1]}
                                    border={border[1]}
                                    borderBottom={`4px solid ${palette.borderColor}`}
                                    position="relative"
                                    bgcolor={palette.contentBg}
                                    sx={{
                                        cursor: "pointer",
                                        transition: "200ms",
                                        ":hover": {
                                            borderColor: palette.primary.light,
                                            transform: `skew(-5deg)`
                                        }
                                    }}
                                >
                                    <Box display="flex" fontSize="20px" color={palette.primary.contrastText} justifyContent="center" width="40px" height="40px" sx={{ aspectRatio: "1 / 1" }} borderRadius="50%" bgcolor={palette.primary.dark} alignItems="center" >{Icons.default.QuizIconOutlined}</Box>
                                    <Box flexGrow={1} minWidth={0} >
                                        <Box display="flex" width="100%" justifyContent="space-between" gap="10px">
                                            <Typography variant='caption' color='textSecondary' noWrap  >{formatDate( practiceSession.date_range_start )} to {formatDate( practiceSession.date_range_end )}</Typography>
                                            <Typography variant='caption' color='textSecondary' noWrap  >{getTimePassedFrom( practiceSession.created_at )}</Typography>
                                        </Box>
                                        <Tooltip placement='top' disableInteractive title={practiceSession.title}>
                                            <Typography textAlign="left" noWrap fontSize={{ xs: "16px", sm: "18px" }} minWidth={0} fontWeight={600}>{practiceSession.title}  </Typography>
                                        </Tooltip>

                                        <Typography display="flex" noWrap alignItems="center" gap="5px" fontSize="12px"  > {Icons.default.Sort2} {`Difficulty level: ${practiceSession.complexity}`}</Typography>
                                        <Typography display="flex" noWrap alignItems="center" gap="5px" fontSize="12px"  > {Icons.default.User} {practiceSession.creator_info.name}</Typography>
                                        <Typography display="flex" noWrap alignItems="center" gap="5px" fontSize="12px" component="em"> {Icons.default.Subjects} {practiceSession.subject?.subject_name} - {modules.join( ", " )}</Typography>
                                        <Typography component="div" display="flex" noWrap alignItems="center" gap="5px" fontSize="12px"  > {Icons.default.Category} {topics.slice( 0, 2 ).map( topic => <CustomChip tooltipTitle={"Practice topic"} value={topic} key={topic} /> )}{topics.length > 2 ? <CustomChip tooltipTitle={topics.slice( 2 ).join( ", " )} value={`+${topics.length - 2} More.`} /> : ""}</Typography>
                                        {/* <Box color={palette.text.secondary} display="flex" gap="5px" fontSize="14px" alignItems="center">
                                            {topics.slice( 0, 2 ).map( ( topic, index ) => (
                                                <Typography title={topic} maxWidth="100px" noWrap fontSize="12px" key={topic} borderRadius="5px" color='textPrimary' border={`1px solid ${palette.text.primary}`} bgcolor={palette.contentBg} padding="2px 5px" >{topic}</Typography>
                                            ) )}
                                            {topics.length > 2 && <Typography fontSize="12px" borderRadius="5px" color='textPrimary' border={`1px solid ${palette.text.primary}`} bgcolor={palette.contentBg} padding="2px 5px" >+{topics.length - 2} More</Typography>}
                                        </Box> */}
                                    </Box>
                                </Box>
                            )
                        } )}
                    </Box>
                </Box>}
                {SimbaAIStore.getPracticeSessions.length === 0 && <Box position="relative" width="100%" border={`2px dashed ${palette.borderColor}`} borderRadius="20px" flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" gap="10px" justifyContent="center" >
                    <Box position="absolute" height="60%" maxHeight="400px" bgcolor={palette.contentBg} borderRadius="50%" sx={{ aspectRatio: "1 / 1", zIndex: 1 }} />
                    <Box position="relative" sx={{ zIndex: 10 }} component="img" src={sadSimba} height="180px" alt="No practice sessions" />
                    <Typography position="relative" sx={{ zIndex: 10 }} textAlign="center" variant="h5" component="p" >No practice sessions to show!</Typography>
                </Box>}
            </Box>
            <Box width="100%" display="flex" position="sticky" bottom="0" justifyContent="space-between" alignItems="center" borderTop={`4px dashed ${palette.borderColor}`} bgcolor={palette.contentBg} padding={{ md: "10px 20px", xs: "10px" }} sx={{ zIndex: 10 }} >
                {Boolean( SimbaAIStore.getPracticeHistory.find( item => item.status === "completed" ) ) && <Button className='animation-wave' onClick={repeatLastOne} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Replay} variant='outlined' color='primary' >Repeat last practice</Button>}
                {!Boolean( SimbaAIStore.getPracticeHistory.find( item => item.status === "completed" ) ) && <Box />}
                <Box display="flex" gap="10px" alignItems="center">
                    <Button onClick={() => setSearchParamsValues( { simbaAIOpen: "", simbaAIMode: "" } )} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Logout} variant='contained' disableElevation color='error' >Quit Simba AI</Button>
                </Box>
            </Box>
        </Box >
    )
} )

export default PracticeModeHome
